import React from 'react'
import ReactAsyncSelect from 'react-select/async'
import ReactAsyncCreatableSelect from 'react-select/async-creatable'

import { WithStyles, withStyles, WithTheme } from '@mui/styles'

import { SelectProps } from '../Base/Select.component'
import { SelectOption } from '../Common/Select.model'
import { selectComponents } from '../Common/Select.partials'
import styles from '../Common/Select.styles'

class AsyncSelect extends React.PureComponent<
  AsyncSelectProps & WithStyles<typeof styles> & WithTheme,
  AsyncSelectState
> {
  readonly state = {
    isLoading: false,
    invalidateCache: false,
  }

  constructor(props: AsyncSelectProps & WithStyles<typeof styles> & WithTheme) {
    super(props)

    this.handleOnCreateOption = this.handleOnCreateOption.bind(this)
    this.inputProps = this.inputProps.bind(this)
    this.onChange = this.onChange.bind(this)
    this.getErrorAndHelperText = this.getErrorAndHelperText.bind(this)
  }

  async handleOnCreateOption(inputValue: string) {
    const { value, onCreateOption, isMulti } = this.props
    if (!onCreateOption) {
      return
    }

    this.setState({
      isLoading: true,
    })

    const newOption = await onCreateOption(inputValue)
    if (newOption) {
      this.setState({
        invalidateCache: true,
      })
      const newValue = isMulti ? ((value as SelectOption[]) || []).concat([newOption]) : newOption
      this.onChange(newValue)
    }

    this.setState({
      isLoading: false,
      invalidateCache: false,
    })
  }

  onChange(value: SelectOption | Array<SelectOption> | null) {
    const { field, form, onChange } = this.props
    let parsedValue: SelectOption | Array<SelectOption> | '' = ''
    if (value && value.constructor === Array) {
      parsedValue = (value as Array<SelectOption>).length > 0 ? value : ''
    } else {
      parsedValue = value || ''
    }
    if (!!onChange) {
      onChange(parsedValue)
    } else if (!!field && !!form) {
      const { setFieldValue } = form
      setFieldValue(field.name, parsedValue)
    }
  }

  inputProps() {
    const { isLoading, invalidateCache } = this.state
    const {
      onCreateOption,
      disabled = false,
      isMulti = false,
      loadOptions,
      field,
      cacheOptions = false,
      ...otherProps
    } = this.props
    return {
      ...field,
      ...otherProps,
      loadOptions,
      onChange: this.onChange,
      components: selectComponents(isLoading),
      isMulti,
      isDisabled: disabled || isLoading,
      defaultOptions: true,
      cacheOptions: !invalidateCache && cacheOptions,
      onCreateOption: !!onCreateOption ? this.handleOnCreateOption : undefined,
      menuPortalTarget: document.body,
    }
  }

  getErrorAndHelperText() {
    const { error, helperText, field, form } = this.props
    if (!!field && !!form) {
      const errorMessage = form.errors[field.name]
      return {
        error: !!errorMessage,
        helperText: !!errorMessage ? errorMessage : helperText,
      }
    } else {
      return {
        error,
        helperText,
      }
    }
  }

  render() {
    const { classes, theme, label, placeholder, disabled = false, onCreateOption } = this.props

    const { isLoading } = this.state

    const selectStyles = {
      input: (base: React.CSSProperties) => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
      menuPortal: (base: React.CSSProperties) => ({ ...base, zIndex: 9999 }),
    }

    const styleProps = {
      classes,
      textFieldProps: {
        label,
        InputLabelProps: {
          shrink: true,
        },
        placeholder,
        disabled,
        variant: isLoading ? 'filled' : 'standard',
      },
    }

    const SelectComponent = (!!onCreateOption ? ReactAsyncCreatableSelect : ReactAsyncSelect) as any

    return <SelectComponent styles={selectStyles} {...this.inputProps()} {...styleProps} />
  }
}

export type AsyncSelectProps = SelectProps & {
  isLoading?: boolean
  cacheOptions?: boolean
  loadOptions?: (inputValue: string, callback: (options: any) => void) => Promise<Array<any> | void> | void
  noOptionsMessage?: (input: { inputValue: string }) => string | null
}

type AsyncSelectState = {
  isLoading: boolean
  invalidateCache: boolean
}

export default withStyles(styles, { withTheme: true })(AsyncSelect)
